<script>
	/**
	 * CorrectModal — the "Alter" modal. Statement-based concept-tree mutation.
	 *
	 * Lane-2 overflow → "Alter" opens this modal keyed to a specific concept.
	 * The user types a natural-language statement and picks a Behaviour from
	 * a four-option dropdown (Add / Correct / Add-or-Correct / Remove). A
	 * static one-line description under the pill explains what an agent will
	 * do for the chosen behaviour — no live AI on the typing path.
	 *
	 * Doctrine (2026-04-28): user-led, AI-advised, audit-transparent. The
	 * user owns the decision (target + behaviour). The AI advises at submit
	 * time (the updater produces the structured operation). The audit trail
	 * records what happened (system log + opt-in email-with-details).
	 *
	 * Submit calls /api/concept/correct with the user's chosen behaviour as
	 * `forced_behaviour`. The backend updater obeys (produces ops compatible
	 * with that behaviour) or no_ops cleanly. No silent operation-type
	 * override — that's the point of the dropdown.
	 *
	 * Acknowledgment shows: outcome line, behaviour + change count, the
	 * applied operations as compact monospace items, and an "Email me the
	 * details" button. Full prose detail lives in the email + system log.
	 *
	 * The earlier live classifier (a Flash call on every keystroke debounce)
	 * was retired 2026-04-28. It added ~1.5s of latency for advisory output
	 * the user doesn't need now that they own the behaviour decision.
	 */
	import { onMount, onDestroy, createEventDispatcher } from 'svelte';
	import { X, ChevronDown } from 'lucide-svelte';
	import { correctConcept, emailAlterationDetails } from '../lib/api.js';

	export let concept;       // { id, concept, context, ... } — the lane-1 selected node (root of the picker)
	export let onClose;       // () => void
	export let onSuccess;     // (result) => void — fires after successful submit
	export let allNodes = []; // full visible-tree node list, used to build the outline picker

	const dispatch = createEventDispatcher();

	// Statement text + chosen behaviour. The behaviour dropdown is the only
	// commitment the user makes besides the statement itself; a static
	// one-liner under the dropdown describes what the agent will do.
	let statement = '';
	let chosenBehaviour = null;      // 'ADD' | 'CORRECT' | 'ADD_OR_CORRECT' | 'REMOVE' | null
	let dropdownOpen = false;

	// Cascade state. When on, the submission scope expands from just
	// `currentTarget` to `currentTarget` + every descendant in the picker
	// (gen ≥ 0.35 — leaves are excluded for visual + scope consistency).
	// Backend receives all in-scope ids and runs ONE updater call across
	// them, producing operations[] across the cascade.
	let cascade = false;

	// Currently editing target — starts as the lane-1 selection but the user
	// can retarget by clicking any branch in the right-column outline picker.
	// The picker tree itself is computed once from `concept` (the lane-1 root)
	// so that retargeting only changes the highlight, not the visible options.
	let currentTarget = concept;

	// Submit state
	let submitting = false;
	let submitError = null;
	let result = null;               // success result, drives ack screen
	let emailing = false;
	let emailed = false;
	let emailError = null;

	// Outline picker — show parent (one level up) + target + branch
	// descendants (gen ≥ 0.35). Leaves below the threshold are excluded so
	// the user only retargets to meaningful concepts, not atomic facts.
	const PICKER_MIN_GENERALITY = 0.35;

	// Mirror ExplorerTree's outline lane formatters so the picker reads as a
	// continuation of that lane, not a separate widget. Keep these inline
	// instead of importing from ExplorerTree — that file isn't a module
	// boundary and dragging it through the modal would invert the dependency.
	function formatGenerality(g) {
		return '.' + ((g || 0) * 100).toFixed(0).padStart(2, '0');
	}
	function tierClass(g) {
		if (g >= 0.65) return 'ct-tier-trunk';
		if (g >= 0.35) return 'ct-tier-branch';
		return 'ct-tier-leaf';
	}

	// User picks a behaviour from the dropdown. Idempotent on same-pick.
	function pickBehaviour(b) {
		dropdownOpen = false;
		if (chosenBehaviour === b) return;
		chosenBehaviour = b;
	}

	function toggleDropdown() {
		if (submitting) return;
		dropdownOpen = !dropdownOpen;
	}

	function selectTarget(node) {
		if (!node || !node.id) return;
		if (currentTarget && node.id === currentTarget.id) return;
		currentTarget = node;
		// User's chosen behaviour and statement persist across retargeting.
		// Cascade resets — the new target's descendant set is different, so
		// keeping the old toggle would carry stale scope.
		cascade = false;
	}

	// Hold-click submit gate. When the chosen combination is destructive
	// (REMOVE behaviour applied across a cascade), require a 3-second
	// pointer hold to commit. Pointer-up before 3s cancels. The visual is
	// a progress fill on the submit button.
	const HOLD_MS = 3000;
	let holdTimer = null;
	let holdProgress = 0;          // 0..1, drives the progress fill
	let holdRafId = null;
	let holdStartedAt = 0;

	$: requiresHoldClick =
		cascade &&
		chosenBehaviour === 'REMOVE' &&
		cascadeIds.length > 1;

	function startHoldClick() {
		if (submitting || !chosenBehaviour || !statement.trim()) return;
		if (!requiresHoldClick) {
			handleSubmit();
			return;
		}
		holdStartedAt = Date.now();
		holdProgress = 0;
		const tick = () => {
			holdProgress = Math.min(1, (Date.now() - holdStartedAt) / HOLD_MS);
			if (holdProgress < 1) {
				holdRafId = requestAnimationFrame(tick);
			}
		};
		holdRafId = requestAnimationFrame(tick);
		holdTimer = setTimeout(() => {
			cancelHoldClick();
			handleSubmit();
		}, HOLD_MS);
	}

	function cancelHoldClick() {
		if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
		if (holdRafId) { cancelAnimationFrame(holdRafId); holdRafId = null; }
		holdProgress = 0;
	}

	async function handleSubmit() {
		if (!statement.trim() || !chosenBehaviour) return;
		if (!cascadeIds.length) return;
		submitting = true;
		submitError = null;
		try {
			const res = await correctConcept({
				conceptIds: cascadeIds,
				statementText: statement,
				forcedBehaviour: chosenBehaviour,
				surface: 'modal',
			});
			result = res;
			if (typeof onSuccess === 'function') onSuccess(res);
		} catch (err) {
			submitError = err.message || String(err);
		} finally {
			submitting = false;
		}
	}

	// Ack-screen "Email me the details" handler. Spare-by-default modal +
	// email-on-demand pattern: the modal stays uncluttered; the user can pull
	// the audit detail into their inbox if they want it.
	async function handleEmailDetails() {
		if (!result || emailing || emailed) return;
		emailing = true;
		emailError = null;
		try {
			await emailAlterationDetails({
				conceptId: result.primary_node_id || (currentTarget && currentTarget.id),
				conceptName: currentTarget && currentTarget.concept,
				provenance: result.provenance,
				noOp: !!result.no_op,
				operationsApplied: result.operations_applied || [],
			});
			emailed = true;
		} catch (err) {
			emailError = err.message || String(err);
		} finally {
			emailing = false;
		}
	}

	function handleClose() {
		if (typeof onClose === 'function') onClose();
		dispatch('close');
	}

	function handleKeydown(e) {
		if (e.key === 'Escape') {
			// ESC layer order: collapse the dropdown first if it's open;
			// otherwise close the whole modal.
			if (dropdownOpen) {
				dropdownOpen = false;
			} else {
				handleClose();
			}
		}
	}

	// Document-level click handler: close the dropdown when the user clicks
	// outside it. The dropdown's own buttons stop propagation via Svelte's
	// element handlers, so this only fires for clicks elsewhere.
	function handleDocClick(e) {
		if (!dropdownOpen) return;
		const root = document.querySelector('.cm-dropdown');
		if (root && !root.contains(e.target)) {
			dropdownOpen = false;
		}
	}

	onMount(() => {
		document.addEventListener('keydown', handleKeydown);
		document.addEventListener('mousedown', handleDocClick);
		// Auto-focus the textarea when modal opens
		setTimeout(() => {
			const ta = document.querySelector('.cm-textarea');
			if (ta) ta.focus();
		}, 50);
	});

	onDestroy(() => {
		document.removeEventListener('keydown', handleKeydown);
		document.removeEventListener('mousedown', handleDocClick);
		cancelHoldClick();
	});

	// First sentence of the concept context for grounding display
	$: contextLead = currentTarget && currentTarget.context
		? (currentTarget.context.match(/^[\s\S]*?[.!?](?=\s|$)/) || [currentTarget.context])[0].trim()
		: '';

	// Build the outline picker rows once from the lane-1 selection. Tree
	// shape doesn't change when the user retargets — only the highlight does.
	// Returns flat list of { node, depth, isAncestor } in display order.
	function buildOutline(rootNode, all) {
		if (!rootNode || !rootNode.id || !Array.isArray(all)) return [];
		const rows = [];
		// Parent (one level up) — surfaced as an ancestor row so the user can
		// promote the correction to the broader concept. Subject to the same
		// generality floor as descendants.
		const parent = rootNode.parent_id
			? all.find(n => n.id === rootNode.parent_id)
			: null;
		if (parent && (parent.generality || 0) >= PICKER_MIN_GENERALITY) {
			rows.push({ node: parent, depth: 0, isAncestor: true });
		}
		const rootDepth = rows.length > 0 ? 1 : 0;
		// Recurse from the root: include every node, but only descend into
		// children that meet the generality floor. The root itself is always
		// shown regardless of its own generality (user's lane-1 selection).
		function walk(node, depth) {
			rows.push({ node, depth, isAncestor: false });
			const children = all
				.filter(n => n.parent_id === node.id && (n.generality || 0) >= PICKER_MIN_GENERALITY)
				.sort((a, b) => (b.generality || 0) - (a.generality || 0));
			for (const c of children) walk(c, depth + 1);
		}
		walk(rootNode, rootDepth);
		return rows;
	}

	$: outlineRows = buildOutline(concept, allNodes);

	// Cascade descendants of `currentTarget` from the outline rows. Walking
	// outlineRows (which is depth-aware) is equivalent to walking allNodes
	// with the gen ≥ 0.35 filter, but it's already cached and respects the
	// picker's traversal order. The target itself is NOT included here —
	// `cascadeIds` below adds it in.
	//
	// outlineRows is passed in explicitly so Svelte's reactive-dependency
	// analyzer sees it. (Reading it inside the helper without exposing it as
	// an argument hid the dependency from the analyzer, producing a
	// "findIndex on undefined" race during the first reactive pass.)
	function descendantIds(targetId, rows) {
		if (!targetId || !Array.isArray(rows) || rows.length === 0) return [];
		const idx = rows.findIndex(r => r.node.id === targetId);
		if (idx < 0) return [];
		const targetDepth = rows[idx].depth;
		const out = [];
		// Walk forward through rows; stop at the first row whose depth is
		// <= targetDepth (that row is a sibling/ancestor — out of scope).
		for (let i = idx + 1; i < rows.length; i++) {
			const row = rows[i];
			if (row.depth <= targetDepth) break;
			// Skip ancestor rows (depth-0 parent) — defensive; they sit
			// before the target in rows so we won't reach them from idx+1,
			// but the predicate keeps the rule clean.
			if (row.isAncestor) continue;
			out.push(row.node.id);
		}
		return out;
	}

	$: cascadeDescendantIds = (currentTarget && Array.isArray(outlineRows))
		? descendantIds(currentTarget.id, outlineRows)
		: [];
	$: cascadeIds = currentTarget && cascade
		? [currentTarget.id, ...cascadeDescendantIds]
		: (currentTarget ? [currentTarget.id] : []);
	$: cascadeIdSet = new Set(cascadeIds);
	$: cascadeAvailable = cascadeDescendantIds.length > 0;
	$: cascadeAdditionalCount = cascade ? cascadeDescendantIds.length : 0;

	// Behaviour → color, label, guidance. Drives the dropdown pill color +
	// menu items + the static one-line description shown under the pill.
	// Order matters in BEHAVIOUR_OPTIONS: green → blue → neutral hybrid → red.
	const BEHAVIOUR_OPTIONS = [
		{
			id: 'ADD', label: 'Add', color: 'green',
			guidance: 'An agent will add your statement to the most appropriate section of this concept.',
		},
		{
			id: 'CORRECT', label: 'Correct', color: 'blue',
			guidance: 'An agent will locate the contradicted text and replace it with your correction. If no exact match is found, no change is applied.',
		},
		{
			id: 'ADD_OR_CORRECT', label: 'Add or Correct', color: 'neutral',
			guidance: 'An agent will add your statement, or replace contradicted text — whichever fits the existing content. The acknowledgment will name which one was applied.',
		},
		{
			id: 'REMOVE', label: 'Remove', color: 'red',
			guidance: 'An agent will archive this concept (or supersede it with a related concept if your statement points at one).',
		},
	];
	$: behaviourOption = chosenBehaviour
		? BEHAVIOUR_OPTIONS.find(o => o.id === chosenBehaviour)
		: null;
	$: behaviourColor = behaviourOption ? behaviourOption.color : 'neutral';
	$: behaviourLabel = behaviourOption ? behaviourOption.label : 'Choose…';
	$: behaviourGuidance = behaviourOption
		? behaviourOption.guidance
		: 'Choose a behaviour to see what an agent will do.';

	// Ack-screen scope label — driven by the result's scope_node_ids count.
	// Single-target (length 1) shows just the concept name; cascade shows
	// "<primary> + N cascade" so the user sees the scope they submitted.
	$: ackScopeSize = result && result.scope_node_ids
		? result.scope_node_ids.length
		: 1;
	$: ackScopeLabel = ackScopeSize > 1
		? `${currentTarget && currentTarget.concept} + ${ackScopeSize - 1} cascade`
		: (currentTarget && currentTarget.concept);

	// Format an applied operation as a compact string for the ack screen list.
	// Examples: "refine on content.warranty-flow", "append to content.auth-flow",
	// "archive (state)", "supersede (state) → bbb-002".
	function formatOpForAck(op) {
		if (!op) return '';
		const fieldRef = op.target_field === 'content' && op.target_subkey
			? `content.${op.target_subkey}`
			: (op.target_field || '');
		switch (op.operation_type) {
			case 'refine_concept': return `refine on ${fieldRef}`;
			case 'append':         return `append to ${fieldRef}`;
			case 'archive':        return `archive (state)`;
			case 'supersede':      return `supersede${op.conflict_with_node_id ? ' → ' + op.conflict_with_node_id.slice(0, 8) : ''}`;
			default:               return `${op.operation_type} on ${fieldRef}`;
		}
	}
</script>

<div class="cm-overlay" on:click|self={handleClose}>
	<div class="cm-modal">
		<header class="cm-head">
			<div class="cm-head-left">
				<div class="cm-head-eyebrow">Editing concept{cascade && cascadeAdditionalCount > 0 ? ` · cascade (+${cascadeAdditionalCount})` : ''}</div>
				<h2 class="cm-head-title">{currentTarget.concept}</h2>
			</div>
			<button class="cm-close" on:click={handleClose} aria-label="Close"><X size={18} strokeWidth={1.5} /></button>
		</header>

		{#if !result}
			<!-- Body splits into the existing input/validation/actions stack on
			     the left and an outline picker on the right. Right column lets
			     the user retarget the correction to a parent or descendant
			     branch without leaving the modal. -->
			<div class="cm-body-row">
				<div class="cm-pane-left">
					<!-- 1. INPUT: type-into box (with hint placeholder). Fixed height. -->
					<textarea
						id="cm-textarea"
						class="cm-textarea"
						placeholder="Tell us what's wrong, missing, or different. e.g. 'PDFs are delivered via Power Automate, not SendGrid.'"
						bind:value={statement}
						maxlength="4000"
						disabled={submitting}
					></textarea>

					<!-- 2. BEHAVIOUR: clickable dropdown pill + static one-line guidance.
					     No live AI; the user picks, the static text describes what an
					     agent will do at submit time. Reserved-height row so the modal
					     doesn't reflow when the dropdown menu opens. -->
					<div class="cm-validation">
						<div class="cm-behaviour-row">
							<span class="cm-behaviour-prefix">Behaviour:</span>
							<div class="cm-dropdown" class:cm-dropdown-open={dropdownOpen}>
								<button
									type="button"
									class="cm-pill cm-pill-{behaviourColor}"
									on:click={toggleDropdown}
									disabled={submitting}
									aria-haspopup="listbox"
									aria-expanded={dropdownOpen}
								>
									<span class="cm-pill-dot"></span>
									<span class="cm-pill-text">{behaviourLabel}</span>
									<ChevronDown size={13} strokeWidth={2} />
								</button>
								{#if dropdownOpen}
									<div class="cm-dropdown-menu" role="listbox" aria-label="Behaviour">
										{#each BEHAVIOUR_OPTIONS as opt}
											<button
												type="button"
												role="option"
												class="cm-dropdown-item cm-dropdown-item-{opt.color}"
												class:is-selected={chosenBehaviour === opt.id}
												on:click={() => pickBehaviour(opt.id)}
												aria-selected={chosenBehaviour === opt.id}
											>
												<span class="cm-pill-dot"></span>
												<span class="cm-dropdown-item-label">{opt.label}</span>
											</button>
										{/each}
									</div>
								{/if}
							</div>
						</div>
						<div
							class="cm-reason"
							class:cm-reason-error={!!submitError}
							class:cm-reason-warn={!submitError && requiresHoldClick}
						>
							{#if submitError}{submitError}
							{:else if requiresHoldClick}This will archive {cascadeIds.length} concepts. Hold the Submit button for 3 seconds to confirm.
							{:else}{behaviourGuidance}{/if}
						</div>
					</div>

					<!-- 3. ACTIONS: submit / cancel. Submit becomes a hold-click
					     gate when the combined choice is destructive (REMOVE +
					     cascade across multiple concepts). -->
					<div class="cm-actions">
						<button class="cm-btn cm-btn-ghost" on:click={handleClose} disabled={submitting}>Cancel</button>
						<button
							class="cm-btn cm-btn-primary"
							class:cm-btn-danger={requiresHoldClick}
							class:cm-btn-holding={holdProgress > 0 && holdProgress < 1}
							style={requiresHoldClick && holdProgress > 0 ? `--cm-hold-progress: ${(holdProgress * 100).toFixed(1)}%` : ''}
							on:pointerdown|preventDefault={startHoldClick}
							on:pointerup={cancelHoldClick}
							on:pointerleave={cancelHoldClick}
							on:pointercancel={cancelHoldClick}
							disabled={submitting || !chosenBehaviour || !statement.trim()}
						>
							<span class="cm-btn-label">{submitting ? 'Applying' : requiresHoldClick ? 'Hold to apply' : 'Submit'}</span>
						</button>
					</div>
				</div>

				<div class="cm-pane-divider" aria-hidden="true"></div>

				<!-- Right column: outline picker + cascade toggle. Click a row to
				     retarget; toggle cascade to expand scope to the chosen target's
				     descendants (gen ≥ 0.35 only — leaves are excluded). When
				     cascade is on, in-scope rows show a lighter shaded highlight. -->
				<div class="cm-pane-right">
					<div class="cm-pane-right-header">
						<span>Apply to</span>
						<label
							class="cm-cascade-toggle"
							class:cm-cascade-toggle-disabled={!cascadeAvailable || submitting}
							title={cascadeAvailable ? 'Apply to this concept and all descendants in the picker' : 'No descendants available'}
						>
							<input
								type="checkbox"
								bind:checked={cascade}
								disabled={!cascadeAvailable || submitting}
							/>
							<span>Cascade</span>
						</label>
					</div>
					<div class="cm-outline" role="listbox" aria-label="Concept target">
						{#each outlineRows as row (row.node.id)}
							{@const isSelected = currentTarget && row.node.id === currentTarget.id}
							{@const isCascaded = cascade && cascadeIdSet.has(row.node.id) && !isSelected}
							<button
								type="button"
								role="option"
								class="cm-outline-row ct-outline-item"
								class:is-selected={isSelected}
								class:is-cascaded={isCascaded}
								class:is-ancestor={row.isAncestor}
								style="padding-left: {8 + row.depth * 12}px"
								on:click={() => selectTarget(row.node)}
								disabled={submitting}
								title={row.node.concept}
								aria-selected={isSelected}
							>
								<span class="ct-generality {tierClass(row.node.generality)}">{formatGenerality(row.node.generality)}</span>
								<span class="ct-outline-label">{row.node.concept}</span>
							</button>
						{/each}
					</div>
				</div>
			</div>
		{:else}
			<!-- Acknowledgment — outcome line + behaviour line + per-operation
			     summary list. Detail lives here; the email just adds prose. The
			     system log captures the full structured detail regardless. -->
			<div class="cm-body cm-ack">
				{#if result.no_op}
					<div class="cm-ack-icon cm-ack-icon-noop">⚪</div>
					<div class="cm-ack-headline">No change applied to {ackScopeLabel}</div>
					<div class="cm-ack-behaviour">
						Behaviour: <strong>{(BEHAVIOUR_OPTIONS.find(o => o.id === result.user_chosen_behaviour) || { label: result.user_chosen_behaviour }).label}</strong>
					</div>
					{#if result.audit_summary}
						<div class="cm-ack-detail">{result.audit_summary}</div>
					{/if}
				{:else}
					<div class="cm-ack-icon cm-ack-icon-ok">✓</div>
					<div class="cm-ack-headline">Applied to {ackScopeLabel}</div>
					<div class="cm-ack-behaviour">
						Behaviour: <strong>{(BEHAVIOUR_OPTIONS.find(o => o.id === result.user_chosen_behaviour) || { label: result.user_chosen_behaviour }).label}</strong>{#if result.system_resolved_to} <span class="cm-ack-resolved">→ resolved to <strong>{result.system_resolved_to === 'ADD' ? 'Add' : 'Correct'}</strong></span>{/if} <span class="cm-ack-count">· {(result.operations_applied || []).length} {(result.operations_applied || []).length === 1 ? 'change' : 'changes'}{ackScopeSize > 1 ? ` across ${ackScopeSize} concepts` : ''}</span>
					</div>
					{#if result.operations_applied && result.operations_applied.length > 0}
						<ul class="cm-ack-ops">
							{#each result.operations_applied as op}
								<li class="cm-ack-op">{formatOpForAck(op)}</li>
							{/each}
						</ul>
					{/if}
				{/if}
				<div class="cm-ack-actions">
					<button
						class="cm-btn cm-btn-ghost"
						on:click={handleEmailDetails}
						disabled={emailing || emailed}
						title={emailError || ''}
					>
						{emailed ? 'Email queued ✓' : emailing ? 'Sending…' : 'Email me the details'}
					</button>
					<button class="cm-btn cm-btn-primary" on:click={handleClose}>Done</button>
				</div>
				{#if emailError}
					<div class="cm-ack-email-error">{emailError}</div>
				{/if}
			</div>
		{/if}
	</div>
</div>

<style>
	/* ──────────────────────────────────────────────────────────────────
	   Aesthetic constraints (per Mike, 2026-04-28):
	   - 1px borders, clean white throughout
	   - Absolutely no resizing or moving elements — every block has a
	     preset height; conditional content lives in a reserved slot
	   - Information flow: input box → validation row → submit/cancel
	   ────────────────────────────────────────────────────────────────── */

	.cm-overlay {
		position: fixed; inset: 0;
		background: rgba(0,0,0,0.32); backdrop-filter: blur(2px);
		display: flex; align-items: flex-start; justify-content: center;
		padding: 64px 24px; z-index: 1100; overflow-y: auto;
	}

	/* Modal: fixed width, fixed height. No flex/grow that would resize on
	   content change. Body has its own scroll if anything overflows.
	   Width holds the input form on the left (480px) + 1px divider +
	   outline picker (399px) for a total of 880px. The right pane is
	   intentionally wider than the actual outline lane so concept names
	   rarely truncate inside the picker. */
	.cm-modal {
		background: #FFFFFF; border-radius: 8px;
		border: 1px solid #E5E7EB;
		box-shadow: 0 8px 32px rgba(0,0,0,0.10);
		width: 880px;                         /* fixed width */
		max-width: 100%;
		display: flex; flex-direction: column;
	}

	/* ── Header — fixed height, 1px bottom border, white background ── */
	.cm-head {
		display: flex; align-items: flex-start; justify-content: space-between;
		padding: 16px 20px;
		background: #FFFFFF; border-bottom: 1px solid #E5E7EB;
		border-radius: 8px 8px 0 0;
		height: 64px;                         /* fixed */
		box-sizing: border-box;
	}
	.cm-head-left { flex: 1; min-width: 0; }
	.cm-head-eyebrow {
		font-size: 10px; font-weight: 600; color: #9CA3AF;
		text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px;
		line-height: 1;
	}
	.cm-head-title {
		font-size: 14px; font-weight: 600; color: #1A1A1A; margin: 0;
		white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
		line-height: 1.3;
	}
	.cm-close {
		display: flex; align-items: center; justify-content: center;
		width: 28px; height: 28px; background: #FFFFFF;
		border: 1px solid #E5E7EB; border-radius: 6px;
		color: #6B7280; cursor: pointer; flex-shrink: 0;
		transition: border-color 120ms, color 120ms;
	}
	.cm-close:hover { border-color: #9CA3AF; color: #1F2937; }

	/* ── Body — fixed-height stack of three reserved sections ── */
	.cm-body { padding: 18px 20px 18px; }

	/* ── Body row — left form column + 1px divider + right outline column.
	   Heights of the two columns are independent; left column is the
	   sum of its fixed-height children, right column matches via
	   align-items: stretch on the flex parent. ── */
	.cm-body-row {
		display: flex; flex-direction: row; align-items: stretch;
	}
	.cm-pane-left {
		width: 480px; box-sizing: border-box;
		padding: 18px 20px 18px;
		flex-shrink: 0;
	}
	.cm-pane-divider {
		width: 1px; background: #E5E7EB; flex-shrink: 0;
	}
	.cm-pane-right {
		flex: 1; min-width: 0;
		box-sizing: border-box;
		padding: 18px 16px 18px;
		display: flex; flex-direction: column;
	}
	.cm-pane-right-header {
		font-size: 10px; font-weight: 600; color: #9CA3AF;
		text-transform: uppercase; letter-spacing: 0.06em;
		margin-bottom: 8px; line-height: 1; padding: 0 6px;
		flex-shrink: 0;
		display: flex; align-items: center; justify-content: space-between;
		gap: 8px;
	}
	/* Cascade toggle — small inline checkbox in the right-pane header.
	   Reads as an option, not a primary affordance. */
	.cm-cascade-toggle {
		display: inline-flex; align-items: center; gap: 5px;
		font-size: 10px; font-weight: 600; letter-spacing: 0.06em;
		text-transform: uppercase; color: #4B5563;
		cursor: pointer;
		padding: 2px 4px;
	}
	.cm-cascade-toggle input { margin: 0; cursor: pointer; }
	.cm-cascade-toggle-disabled {
		color: #C4C9D4; cursor: not-allowed;
	}
	.cm-cascade-toggle-disabled input { cursor: not-allowed; }
	.cm-outline {
		flex: 1; min-height: 0; overflow-y: auto;
		display: flex; flex-direction: column;
		padding-right: 2px;                   /* room for scrollbar */
	}
	/* Outline row — visually identical to ExplorerTree's outline lane:
	   9px score (tier-colored, tabular-nums) + 10px label, baseline align,
	   5px gap. Reset the <button> defaults so it reads as a row, not a
	   button. Selected/ancestor variants tweak background + emphasis only. */
	.cm-outline-row {
		display: flex; align-items: baseline; gap: 5px;
		flex-shrink: 0;
		padding: 5px 8px;                     /* left padding overridden inline by depth */
		font-family: inherit;
		background: #FFFFFF; border: none;
		border-left: 2px solid transparent;
		cursor: pointer; text-align: left; width: 100%;
		transition: background 80ms, border-color 80ms;
	}
	.cm-outline-row:hover:not(:disabled) { background: #E8EBF0; }
	.cm-outline-row:disabled { cursor: not-allowed; opacity: 0.55; }
	.cm-outline-row :global(.ct-outline-label) {
		font-size: 10px; color: #6B7280;
		white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
		flex: 1; min-width: 0;
	}
	.cm-outline-row :global(.ct-generality) {
		flex-shrink: 0; font-size: 9px; font-weight: 600;
		font-variant-numeric: tabular-nums;
		min-width: 22px; text-align: right;
	}
	.cm-outline-row :global(.ct-tier-trunk)  { color: #6B7280; }
	.cm-outline-row :global(.ct-tier-branch) { color: #9CA3AF; }
	.cm-outline-row :global(.ct-tier-leaf)   { color: #C4C9D4; }
	.cm-outline-row.is-ancestor :global(.ct-outline-label) { color: #8A90A0; font-style: italic; }
	.cm-outline-row.is-selected {
		background: #EEF2F7;
		border-left-color: #1F2937;
	}
	.cm-outline-row.is-selected :global(.ct-outline-label) {
		color: #111827; font-weight: 600; font-style: normal;
	}
	/* Cascade-included rows — same selected hue, lighter fill, no border
	   accent. Reads as "in scope" without competing with the primary target. */
	.cm-outline-row.is-cascaded {
		background: #F4F6FA;
		border-left-color: #9CA3AF;
	}
	.cm-outline-row.is-cascaded :global(.ct-outline-label) {
		color: #1F2937; font-style: normal;
	}

	/* 1. INPUT — textarea, fixed height, no resize, 1px border */
	.cm-textarea {
		width: 100%; box-sizing: border-box;
		font-family: inherit; font-size: 13px; line-height: 1.55;
		color: #1F2937;
		background: #FFFFFF; border: 1px solid #D8DBE2; border-radius: 6px;
		padding: 12px 14px;
		resize: none;                         /* no manual resize */
		height: 116px;                        /* fixed height ≈ 4 lines */
		transition: border-color 120ms;
	}
	.cm-textarea::placeholder { color: #9CA3AF; }
	.cm-textarea:focus { outline: none; border-color: #6B7E94; }
	.cm-textarea:disabled { background: #FAFBFC; color: #9CA3AF; }

	/* 2. BEHAVIOUR + REASON — fixed-height row reserved regardless of state.
	   Behaviour pill on top, reason text underneath, both have reserved space.
	   Total height: pill 26px + gap 6px + reason 3 lines (~50px) = 82px.
	   Dropdown menu opens absolutely-positioned so the layout doesn't shift. */
	.cm-validation {
		margin-top: 14px;
		height: 82px;                         /* fixed — anti-flicker reservation */
		display: flex; flex-direction: column; gap: 6px;
	}
	.cm-behaviour-row {
		display: flex; align-items: center; gap: 10px;
		height: 26px; box-sizing: border-box;
	}
	.cm-behaviour-prefix {
		font-size: 11.5px; color: #6B7280; font-weight: 500;
		letter-spacing: 0.01em;
	}
	.cm-dropdown {
		position: relative;
	}

	/* Pill — same height/shape as the previous passive badge, now clickable.
	   Color follows chosen behaviour. ChevronDown affordance signals the menu. */
	.cm-pill {
		display: inline-flex; align-items: center; gap: 8px;
		padding: 0 12px; border-radius: 999px;
		font-family: inherit;
		font-size: 11.5px; font-weight: 600; letter-spacing: 0.02em;
		background: #FFFFFF; border: 1px solid #E5E7EB; color: #6B7280;
		height: 26px; box-sizing: border-box;
		cursor: pointer;
		transition: background 240ms ease-out, color 240ms ease-out, border-color 240ms ease-out, box-shadow 120ms;
	}
	.cm-pill:hover:not(:disabled) { box-shadow: 0 0 0 1px rgba(31,41,55,0.06); }
	.cm-pill:disabled { cursor: not-allowed; opacity: 0.55; }
	.cm-pill-dot {
		display: inline-block; width: 8px; height: 8px; border-radius: 50%;
		flex-shrink: 0; background: #C5C8CD;
		transition: background 240ms ease-out;
	}
	.cm-pill-text { white-space: nowrap; }

	/* Color states — match badge palette so the visual language is consistent. */
	.cm-pill-neutral { background: #FFFFFF; color: #6B7280; border-color: #D8DBE2; }
	.cm-pill-neutral .cm-pill-dot { background: #C5C8CD; }
	.cm-pill-green   { background: #FFFFFF; color: #166534; border-color: #BBE5C7; }
	.cm-pill-green   .cm-pill-dot { background: #16A34A; }
	.cm-pill-blue    { background: #FFFFFF; color: #1E40AF; border-color: #BFD8FF; }
	.cm-pill-blue    .cm-pill-dot { background: #2563EB; }
	.cm-pill-red     { background: #FFFFFF; color: #9F1239; border-color: #FCC5CC; }
	.cm-pill-red     .cm-pill-dot { background: #DC2626; }

	/* Dropdown menu — absolute-positioned below the pill so opening it
	   doesn't shift the validation row's reserved height. 1px borders on
	   white surface, identical aesthetic to the rest of the modal. */
	.cm-dropdown-menu {
		position: absolute;
		top: calc(100% + 4px); left: 0;
		min-width: 180px;
		background: #FFFFFF;
		border: 1px solid #D8DBE2;
		border-radius: 6px;
		box-shadow: 0 4px 12px rgba(0,0,0,0.08);
		padding: 4px;
		z-index: 1200;
		display: flex; flex-direction: column; gap: 1px;
	}
	.cm-dropdown-item {
		display: flex; align-items: center; gap: 8px;
		padding: 6px 10px;
		font-family: inherit; font-size: 11.5px; font-weight: 500;
		background: #FFFFFF; border: none; border-radius: 4px;
		cursor: pointer; text-align: left; width: 100%;
		color: #1F2937;
		transition: background 80ms;
	}
	.cm-dropdown-item:hover { background: #F3F4F6; }
	.cm-dropdown-item-label { flex: 1; }
	.cm-dropdown-item.is-selected {
		background: #F3F4F6;
		color: #111827; font-weight: 600;
	}
	/* Per-option dot color — matches the pill colors. */
	.cm-dropdown-item-green   .cm-pill-dot { background: #16A34A; }
	.cm-dropdown-item-blue    .cm-pill-dot { background: #2563EB; }
	.cm-dropdown-item-red     .cm-pill-dot { background: #DC2626; }
	.cm-dropdown-item-neutral .cm-pill-dot { background: #C5C8CD; }

	/* Static behaviour guidance — fixed-height reserved area. Reads as
	   informational copy (not a status), so non-italic, gray-500.
	   Reserved for up to 3 lines: 11.5px × 1.45 × 3 ≈ 50px. */
	.cm-reason {
		font-size: 11.5px; color: #6B7280; line-height: 1.45;
		height: 50px;
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
	}
	.cm-reason-error { color: #9F1239; font-style: italic; }
	.cm-reason-warn  { color: #9F1239; font-weight: 500; }

	/* 3. ACTIONS — fixed-height footer with 1px top border */
	.cm-actions {
		display: flex; justify-content: flex-end; gap: 8px;
		margin-top: 16px;
		padding-top: 14px;
		border-top: 1px solid #E5E7EB;
		height: 56px; box-sizing: border-box;
	}
	.cm-btn {
		font-family: inherit; font-size: 12.5px; font-weight: 500;
		padding: 0 16px; height: 32px;        /* fixed height */
		border-radius: 6px; cursor: pointer;
		transition: background 120ms ease-out, color 120ms ease-out, border-color 120ms ease-out;
		min-width: 84px;                      /* prevent width jitter on label change */
	}
	.cm-btn:disabled { cursor: not-allowed; opacity: 0.45; }
	.cm-btn-ghost   { background: #FFFFFF; color: #6B7280; border: 1px solid #D8DBE2; }
	.cm-btn-ghost:hover:not(:disabled)   { border-color: #9CA3AF; color: #1F2937; }
	.cm-btn-primary { background: #1F2937; color: #FFFFFF; border: 1px solid #1F2937; }
	.cm-btn-primary:hover:not(:disabled) { background: #111827; border-color: #111827; }

	/* Danger variant — hold-to-confirm submit button when REMOVE+cascade.
	   Red base + a left-to-right progress fill driven by --cm-hold-progress
	   (set inline by Svelte). The fill sits as a translucent overlay on top
	   of the red background so the user sees the gauge filling as they hold. */
	.cm-btn-danger {
		background: #B91C1C;
		border-color: #B91C1C;
		position: relative;
		overflow: hidden;
		min-width: 120px;
	}
	.cm-btn-danger:hover:not(:disabled) {
		background: #991B1B;
		border-color: #991B1B;
	}
	.cm-btn-danger .cm-btn-label {
		position: relative;
		z-index: 1;
	}
	.cm-btn-danger.cm-btn-holding::before {
		content: '';
		position: absolute;
		top: 0; left: 0; bottom: 0;
		width: var(--cm-hold-progress, 0%);
		background: rgba(255, 255, 255, 0.22);
		transition: width 60ms linear;
		z-index: 0;
	}

	/* ── Acknowledgment screen — SPARE by default. Replaces input form +
	   right pane on success. Single concept name + behaviour + two buttons.
	   Detail lives in the email + system log, not in the modal. ── */
	.cm-ack {
		text-align: center;
		padding: 36px 32px 24px;
		min-height: 320px;                    /* match body height so the modal doesn't resize */
		box-sizing: border-box;
		display: flex; flex-direction: column; align-items: center; justify-content: center;
	}
	.cm-ack-icon  { font-size: 36px; line-height: 1; margin-bottom: 14px; }
	.cm-ack-icon-ok   { color: #16A34A; }
	.cm-ack-icon-noop { color: #9CA3AF; }
	.cm-ack-headline {
		font-size: 15px; font-weight: 600; color: #111827; margin-bottom: 10px;
		max-width: 560px;
	}
	.cm-ack-behaviour {
		font-size: 12.5px; color: #4B5563; margin-bottom: 14px;
	}
	.cm-ack-behaviour strong { color: #1F2937; font-weight: 600; }
	.cm-ack-resolved { color: #4B5563; }
	.cm-ack-count    { color: #6B7280; }
	.cm-ack-detail {
		font-size: 12px; color: #6B7280; line-height: 1.5;
		max-width: 520px; margin: 0 0 14px;
	}
	.cm-ack-ops {
		list-style: none;
		padding: 0;
		margin: 0 0 22px;
		display: flex; flex-direction: column; gap: 4px;
		align-items: center;
	}
	.cm-ack-op {
		font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
		font-size: 11.5px;
		color: #4B5563;
		padding: 2px 10px;
		background: #F9FAFB;
		border: 1px solid #E5E7EB;
		border-radius: 4px;
	}
	.cm-ack-actions {
		display: flex; gap: 8px; justify-content: center;
	}
	.cm-ack-email-error {
		margin-top: 12px;
		font-size: 11.5px; color: #9F1239;
		max-width: 380px;
	}
</style>
